<template>
  <v-dialog v-model="dialogProxy" persistent max-width="500px">
    <v-card>
      <v-card-title>
        <span class="headline">
          {{
            selectedOrders.length
              ? $t('app.btn.downloadSelection')
              : $t('app.btn.downloadAll')
          }}
        </span>
      </v-card-title>

      <v-card-text>
        <div>
          {{ $t('converter.download.text') }}
        </div>
        <v-progress-linear
          :value="progress"
          class="mt-4"
          color="#ECC52B"
          height="50"
        >
          <div class="font-weight-bold">{{ progress.toFixed() }}%</div>
        </v-progress-linear>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="$emit('close')">
          {{ $t('app.btn.cancel') }}
        </v-btn>
        <v-btn color="blue darken-1" text @click="downloadAllFiles(false)">
          XSD 3.1
        </v-btn>
        <v-btn color="blue darken-1" text @click="downloadAllFiles(true)">
          XSD 5.0
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import assetService from '@/service/assetService';
import { parseFile } from '@/xsd-mappings';
import JSZip from 'jszip';
import { defineComponent, PropType } from 'vue';
import { Order } from '@/types/Order';

export default defineComponent({
  name: 'DownloadDialog',
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    allOrders: {
      type: Array as PropType<Array<Order>>,
      required: true,
    },
    selectedOrders: {
      type: Array as PropType<Array<Order>>,
      required: true,
    },
  },
  data(): { progress: number; realProgress: number } {
    return {
      progress: 0,
      realProgress: 0,
    };
  },
  computed: {
    dialogProxy: {
      // eslint-disable-next-line
      get: function (): boolean {
        return this.dialog;
      },
      // eslint-disable-next-line
      set: function (value: boolean) {
        this.$emit('update:dialog', value);
      },
    },
  },
  methods: {
    async downloadAllFiles(convertXmlFiles: boolean) {
      const orderList = this.selectedOrders.length
        ? this.selectedOrders
        : this.allOrders;
      this.progress = 0;

      const progressUpdater = setInterval(() => {
        this.progress = this.realProgress;
      }, 100);
      const orderSize = orderList.length;

      for (const order of orderList) {
        const assetResponse = await assetService.getAssetDTOsFromApi(order.id);
        const assetList = assetResponse?.data ?? [];
        if (!assetList.length) {
          continue;
        }

        const zipFile = new JSZip();
        for (const asset of assetList) {
          const assetBlobResponse = await assetService.downloadAssetFromApi(
            asset.id
          );
          let blob = assetBlobResponse?.data;
          if (!blob) continue;

          if (
            convertXmlFiles &&
            (asset.type === 'EXPORT_XML' || asset.type === 'ZIP')
          ) {
            const isZip = asset.type === 'ZIP';
            blob = await parseFile(
              blob,
              isZip,
              (additionalProgress: number) => {
                const newProgress =
                  additionalProgress / (orderSize * assetResponse?.data.length);
                this.realProgress += newProgress;
              }
            );
          } else {
            this.realProgress += 100 / (orderSize * assetResponse?.data.length);
          }
          zipFile.file(asset.filename, blob);
        }
        const orderZip = await zipFile.generateAsync({
          type: 'blob',
          streamFiles: true,
        });
        const dateString = new Date()
          .toISOString()
          .slice(0, 10)
          .replaceAll('-', '');
        const xmlVersion = convertXmlFiles ? 'v5_0' : 'v3_1';
        const zipName = `VPI_MaintenanceTool_AllOrders_${dateString}_${xmlVersion}.zip`;
        this.downloadBlob(orderZip, zipName);
      }

      clearInterval(progressUpdater);
      this.closeAndReset();
    },
    downloadBlob(blob: Blob, name: string) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    },
    closeAndReset() {
      this.progress = 0;
      this.realProgress = 0;
      this.$emit('close');
    },
  },
});
</script>
